import React from 'react';
import { Sunrise, Sun, Moon } from 'lucide-react';

export interface VisitCardProps {
  parkName: string;
  visitDate: string;
  visitPeriod?: 'MORNING' | 'MIDDAY' | 'EVENING';
  attractionCount: number;
  /** primary: gold-accented hero card (today / tomorrow-as-primary); default: standard list card */
  variant?: 'primary' | 'default';
  /** Optional status pill rendered on the right (e.g. Active / Upcoming / Completed) */
  status?: { label: string; color: string };
  /** Suppress dark-mode ring when the card is wrapped in SwipeableCard (which owns the ring) */
  noRing?: boolean;
  onClick: () => void;
  'aria-label'?: string;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function PeriodPill({ period }: { period: 'MORNING' | 'MIDDAY' | 'EVENING' }) {
  const map = {
    MORNING: { label: 'Morning', Icon: Sunrise, color: 'text-amber-500 dark:text-amber-300' },
    MIDDAY:  { label: 'Midday',  Icon: Sun,     color: 'text-orange-500 dark:text-orange-300' },
    EVENING: { label: 'Evening', Icon: Moon,    color: 'text-indigo-500 dark:text-indigo-300' },
  };
  const { label, Icon, color } = map[period];
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium ${color}`}>
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </span>
  );
}

export function VisitCard({
  parkName,
  visitDate,
  visitPeriod,
  attractionCount,
  variant = 'default',
  status,
  noRing = false,
  onClick,
  'aria-label': ariaLabel,
}: VisitCardProps): React.ReactElement {
  const isPrimary = variant === 'primary';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? `View visit to ${parkName} on ${formatDate(visitDate)}`}
      className={[
        'w-full bg-white dark:bg-surface-dark-card rounded-xl shadow-card hover:shadow-card-hover transition-all duration-200 text-left',
        noRing ? '' : 'dark:ring-1 dark:ring-white/[0.06]',
        isPrimary ? 'p-5 border-l-4 border-disney-gold-400' : 'p-4',
      ].join(' ')}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className={isPrimary ? 'text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight' : 'font-bold text-gray-900 dark:text-gray-100'}>
            {parkName}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{formatDate(visitDate)}</p>
          {visitPeriod && <div className="mt-1"><PeriodPill period={visitPeriod} /></div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {status && (
            <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${status.color}`}>
              {status.label}
            </span>
          )}
          {!isPrimary && (
            <span className="text-xs text-disney-blue-600 dark:text-disney-blue-400">
              {attractionCount} attraction{attractionCount !== 1 ? 's' : ''}
            </span>
          )}
          {isPrimary && (
            <span className="text-sm font-medium text-disney-blue-600 dark:text-disney-blue-400">
              {attractionCount} attraction{attractionCount !== 1 ? 's' : ''}
            </span>
          )}
          <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
}
